import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Box, Typography, Button, Avatar, IconButton, CircularProgress } from "@mui/material";
import {
    PersonOutline,
    SecurityOutlined,
    VisibilityOutlined,
    NotificationsOutlined,
    EditOutlined,
    PhotoCamera,
    Palette,
    ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { useAppDispatch } from "../../store/hook";
import { RootState } from "../../store/store";
import { selectProfilePhoto, selectUserLocation } from "../../store/slices/profileSlice";
import ProfileInfoTab from "./tabs/ProfileInfoTab";
import AccountTab from "./tabs/AccountTab";
import PrivacyTab from "./tabs/PrivacyTab";
import NotificationsTab from "./tabs/NotificationsTab";
import AppearanceTab from "./tabs/AppearanceTab";
import classes from "./Settings.module.scss";
import useResponsive from "../../hooks/useResponsive";

// Tab types for type safety
type TabTypes = "profile" | "account" | "privacy" | "notifications" | "appearance";

const Settings = () => {
    const loading = useSelector((state: RootState) => state.profile.loading);
    const { isTabletUp, isMobile } = useResponsive();

    // Active tab state
    const [activeTab, setActiveTab] = useState<TabTypes>("profile");
    // State for mobile content visibility
    const [showContentMobile, setShowContentMobile] = useState(false);

    // Return loading state if profile data is being fetched
    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="70vh">
                <CircularProgress />
                <Typography variant="h6" sx={{ ml: 2 }}>
                    Loading Settings...
                </Typography>
            </Box>
        );
    }

    // Define tabs with their icons and labels
    const tabs = [
        {
            id: "profile" as TabTypes,
            label: "Profile Information",
            icon: <PersonOutline className={classes.icon} />,
        },
        {
            id: "account" as TabTypes,
            label: "Account Settings (Beta)",
            icon: <SecurityOutlined className={classes.icon} />,
        },
        {
            id: "privacy" as TabTypes,
            label: "Privacy (Beta)",
            icon: <VisibilityOutlined className={classes.icon} />,
        },
        {
            id: "notifications" as TabTypes,
            label: "Notifications (Beta)",
            icon: <NotificationsOutlined className={classes.icon} />,
        },
        {
            id: "appearance" as TabTypes,
            label: "Appearance",
            icon: <Palette className={classes.icon} />,
        },
    ];

    const handleTabClick = (tabId: TabTypes) => {
        setActiveTab(tabId);
        if (!isTabletUp) {
            setShowContentMobile(true);
        }
    };

    const handleMobileBack = () => {
        setShowContentMobile(false);
    };

    // Render the appropriate tab content
    const renderTabContent = () => {
        switch (activeTab) {
            case "profile":
                return <ProfileInfoTab />;
            case "account":
                return <AccountTab />;
            case "privacy":
                return <PrivacyTab />;
            case "notifications":
                return <NotificationsTab />;
            case "appearance":
                return <AppearanceTab />;
            default:
                return <ProfileInfoTab />;
        }
    };

    return (
        <div
            className={`${
                classes.settingsPageContainer
            } ${!isTabletUp ? classes.mobileSettingsContainer : ""}`}
        >
            {/* Left Side Tabs */}
            <div
                className={`${classes.tabContainer} ${!isTabletUp ? classes.mobilePane : ""} ${
                    !isTabletUp && showContentMobile ? classes.tabsHidden : ""
                }`}
            >
                {tabs.map((tab) => (
                    <div
                        key={tab.id}
                        className={`${classes.tabItem} ${activeTab === tab.id ? classes.active : ""}`}
                        onClick={() => handleTabClick(tab.id)}
                    >
                        {tab.icon}
                        <span>{tab.label}</span>
                    </div>
                ))}
            </div>

            {/* Right Side Content */}
            <div
                className={`${classes.contentContainer} ${!isTabletUp ? classes.mobilePane : ""} ${
                    !isTabletUp && showContentMobile ? classes.contentShown : ""
                }`}
            >
                {!isTabletUp && showContentMobile && (
                    <IconButton
                        onClick={handleMobileBack}
                        sx={{
                            position: "absolute",
                            top: "1rem",
                            left: "1rem",
                            zIndex: 3,
                            // backgroundColor: "rgba(255,255,255,0.7)",
                            "&:hover": { backgroundColor: "rgb(181,161,101,0.9)" },
                            marginRight: "1rem",
                        }}
                        aria-label="back to settings tabs"
                    >
                        <ArrowBackIcon />
                    </IconButton>
                )}
                {renderTabContent()}
            </div>
        </div>
    );
};

export default Settings;
